import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Chip, Grid, MenuItem, TextField } from '@mui/material';
import { keyBy } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { connect, useDispatch } from 'react-redux';
import * as yup from 'yup';
import { SchemaOf } from 'yup';
import { FormAutocomplete, FormCheckbox, FormSelect, FormTextField } from '../../common/form';
import {
    AgreementDto,
    AgreementStatus,
    AgreementType,
    CustomerConnectionType,
    UpdateAgreementDto
} from '../../common/PricePrgClient';
import { RootState } from '../../store';
import { toDateString } from '../../utils';
import { getCustomers } from '../customer/redux/customerSelectors';
import { showConfirmDialog } from '../ui/redux/uiSlices';
import { getCurrentUserPermissions } from '../user/redux/userSelectors';
import { getAvailableUserGroups } from '../usergroup/redux/userGroupSelectors';
import AgreementTags from './AgreementTags';
import CopyAgreementDialog from './CopyAgreementDialog';
import { getSelectedAgreement } from './redux/agreementSelectors';
import { deleteAgreement, showCopyDialog, updateAgreement } from './redux/agreementSlices';

const mapStateToProps = (state: RootState) => ({
    selectedAgreement: getSelectedAgreement(state),
    isShowingCopyDialog: state.agreement.ui.isShowingCopyDialog,
    customers: getCustomers(state),
    customerGroups: state.customerGroups.customerGroups,
    userGroups: getAvailableUserGroups(state),
    isAdmin: getCurrentUserPermissions(state).isAdmin
});

type Props = ReturnType<typeof mapStateToProps>;

const schema: SchemaOf<UpdateAgreementDto> = yup.object().shape({
    agreementId: yup.number().required().label('Agreement id'),
    name: yup.string().required().max(60).label('Name'),
    description: yup.string().optional().label('Description'),
    agreementStatus: yup
        .mixed<AgreementStatus>()
        .oneOf([AgreementStatus.Inactive, AgreementStatus.Published, AgreementStatus.New])
        .required()
        .label('Agreement status'),
    handleExternalPrices: yup.boolean().required().label('Handle external prices'),
    permittedUserGroups: yup.array(yup.number().required()).default([])
});

function ToUpdateAgreementDto(agreement: AgreementDto | null) {
    return {
        agreementId: agreement?.agreementId,
        name: agreement?.name,
        description: agreement?.description,
        agreementStatus: agreement?.agreementStatus,
        handleExternalPrices: agreement?.handleExternalPrices,
        permittedUserGroups: agreement?.permittedUserGroups.map(c => c.userGroupId) ?? []
    };
}

const AgreementDetails = ({
    selectedAgreement,
    isShowingCopyDialog,
    customers,
    customerGroups,
    userGroups,
    isAdmin
}: Props) => {
    const [agreementState, setAgreementState] = useState<AgreementDto | null>(selectedAgreement);

    const { control, handleSubmit, reset } = useForm<UpdateAgreementDto>({
        defaultValues: useMemo(() => ToUpdateAgreementDto(selectedAgreement), [selectedAgreement]),
        resolver: yupResolver(schema)
    });

    const availableUserGroups = useMemo(
        () => keyBy(userGroups.concat(selectedAgreement?.permittedUserGroups ?? []), x => x.userGroupId),
        [userGroups, selectedAgreement]
    );

    const frozenUserGroupIds = useMemo(() => {
        if (selectedAgreement == null || isAdmin) {
            return [];
        }

        return selectedAgreement.permittedUserGroups
            .filter(agUg => userGroups.every(userUg => userUg.userGroupId !== agUg.userGroupId))
            .map(ug => ug.userGroupId);
    }, [userGroups, selectedAgreement, isAdmin]);

    const dispatch = useDispatch();

    useEffect(() => {
        if (selectedAgreement != null) {
            setAgreementState(selectedAgreement);
            reset(ToUpdateAgreementDto(selectedAgreement));
        }
    }, [reset, selectedAgreement]);

    const readonly = useMemo(
        () => selectedAgreement?.agreementStatus !== AgreementStatus.New,
        [selectedAgreement]
    );
    const isStatusFieldReadonly = useMemo(() => {
        return selectedAgreement?.agreementStatus === AgreementStatus.Inactive;
    }, [selectedAgreement]);

    if (agreementState == null) {
        return null;
    }

    const saveAgreementDetails = (dto: UpdateAgreementDto) => {
        dispatch(updateAgreement.request(dto));
    };

    const handleDeleteAgreement = () => {
        dispatch(
            showConfirmDialog({
                primaryActionName: 'Delete',
                okAction: deleteAgreement.request(agreementState.agreementId),
                dialogTitle: 'Delete agreement',
                dialogMessage: `Do you want to delete agreement ${agreementState.name}?`
            })
        );
    };

    const getCustomerName = (customerId: number | undefined) => {
        if (customerId == null) {
            return '';
        }

        const customer = customers[customerId];
        if (customer == null) {
            return '';
        }

        return `${customer.customerNumber} - ${customer.name}`;
    };

    const getCustomerGroupName = (customerGroupId: number | undefined) => {
        if (customerGroupId == null) {
            return '';
        }

        const custGroup = customerGroups[customerGroupId];
        if (custGroup == null) {
            return '';
        }

        return custGroup.name;
    };

    const getName = (userGroupId: number) => availableUserGroups[String(userGroupId)]?.name ?? '??';

    return (
        <>
            <form
                onSubmit={handleSubmit(form => {
                    saveAgreementDetails(form);
                })}
            >
                <Grid container spacing={2} direction="column">
                    <Grid item>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    autoFocus
                                    variant="standard"
                                    label="Name"
                                    name="name"
                                    control={control}
                                    disabled={readonly}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    label="Description"
                                    name="description"
                                    control={control}
                                    disabled={readonly}
                                    variant="standard"
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    label="Currency"
                                    variant="standard"
                                    value={agreementState.currency}
                                    disabled={true}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormSelect
                                    name="agreementStatus"
                                    control={control}
                                    disabled={isStatusFieldReadonly}
                                >
                                    <MenuItem
                                        key={AgreementStatus.New}
                                        value={AgreementStatus.New}
                                        disabled={
                                            (agreementState.agreementStatus ?? 0) >= AgreementStatus.Published
                                        }
                                    >
                                        New
                                    </MenuItem>
                                    <MenuItem
                                        key={AgreementStatus.Published}
                                        value={AgreementStatus.Published}
                                    >
                                        Published
                                    </MenuItem>
                                    <MenuItem
                                        disabled={
                                            !agreementState.externalReference &&
                                            agreementState.agreementStatus == AgreementStatus.Published
                                        }
                                        key={AgreementStatus.Inactive}
                                        value={AgreementStatus.Inactive}
                                    >
                                        Inactive
                                    </MenuItem>
                                </FormSelect>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    label="Valid from"
                                    value={toDateString(agreementState.validFrom)}
                                    disabled={true}
                                    variant="standard"
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    label="Valid to"
                                    value={toDateString(agreementState.validTo)}
                                    disabled={true}
                                    variant="standard"
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    label="External reference"
                                    value={
                                        agreementState.externalReference != null &&
                                        agreementState.externalReference.length > 0
                                            ? agreementState.externalReference
                                            : ' '
                                    }
                                    disabled={true}
                                    variant="standard"
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    label="Customer connection"
                                    variant="standard"
                                    value={
                                        agreementState.customerConnectionType ===
                                        CustomerConnectionType.Customer
                                            ? 'Customer'
                                            : agreementState.customerConnectionType ===
                                              CustomerConnectionType.BusinessChain
                                            ? 'Business chain'
                                            : agreementState.customerConnectionType ===
                                              CustomerConnectionType.CustomerGroup
                                            ? ' Customer group'
                                            : ''
                                    }
                                    disabled={true}
                                    style={{ width: '70%' }}
                                />
                            </Grid>

                            {[CustomerConnectionType.Customer, CustomerConnectionType.BusinessChain].includes(
                                agreementState.customerConnectionType
                            ) && (
                                <Grid item xs={12} md={5}>
                                    <TextField
                                        label="Customer"
                                        value={getCustomerName(agreementState.customerId)}
                                        disabled={true}
                                        style={{ width: '70%' }}
                                        variant="standard"
                                    />
                                </Grid>
                            )}
                            {agreementState.customerConnectionType ===
                                CustomerConnectionType.CustomerGroup && (
                                <Grid item xs={12} md={5}>
                                    <TextField
                                        label="Customer group"
                                        value={getCustomerGroupName(agreementState.customerGroupId)}
                                        disabled={true}
                                        style={{ width: '70%' }}
                                        variant="standard"
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                    <Grid item xs={12} md={6} lg={4}>
                        <FormAutocomplete
                            control={control}
                            multiple={true}
                            name="permittedUserGroups"
                            options={Object.keys(availableUserGroups).map(Number)}
                            getOptionDisabled={option => frozenUserGroupIds.includes(option)}
                            label="Permitted user groups"
                            getOptionLabel={userGroupId => availableUserGroups[userGroupId]?.name ?? '??'}
                            renderTags={(tagValue, getTagProps) =>
                                tagValue
                                    .sort((a, b) => getName(a).localeCompare(getName(b)))
                                    .map((option, index) => (
                                        <Chip
                                            label={getName(option)}
                                            {...getTagProps({ index })}
                                            key={option}
                                            disabled={frozenUserGroupIds.includes(option)}
                                        />
                                    ))
                            }
                            isOptionEqualToValue={(options, values) => options === values}
                            disabled={agreementState.agreementStatus === AgreementStatus.Inactive}
                        />
                    </Grid>
                    <Grid item>
                        <AgreementTags agreementTags={agreementState.tags ?? []} />
                    </Grid>
                    <Grid item>
                        <Grid container>
                            <Grid item xs={12} md={3} style={{ marginRight: 4 }}>
                                <TextField
                                    label="Agreement type"
                                    value={AgreementType[agreementState.agreementType]}
                                    disabled={true}
                                    variant="standard"
                                />
                            </Grid>

                            {agreementState.agreementType === AgreementType.Preorder && (
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        label="Preorder date"
                                        value={
                                            agreementState.preorderDate != null
                                                ? toDateString(
                                                      agreementState.preorderDate,
                                                      'yyyy-MM-dd HH:mm'
                                                  )
                                                : ''
                                        }
                                        disabled={true}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <FormCheckbox
                            name="handleExternalPrices"
                            label="Handle external prices"
                            control={control}
                            disabled={readonly}
                        />
                    </Grid>
                </Grid>
                {selectedAgreement?.publishedDate != null && (
                    <Box sx={{ fontStyle: 'italic', fontSize: '0.875rem', paddingBottom: 2 }}>
                        Published {toDateString(selectedAgreement.publishedDate, 'yyyy-MM-dd HH:mm')}
                    </Box>
                )}
                <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                        {!isStatusFieldReadonly && (
                            <Button variant="contained" color="primary" type="submit">
                                Save
                            </Button>
                        )}
                    </Grid>
                    <Grid item md={6}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => dispatch(showCopyDialog(true))}
                        >
                            Copy
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => handleDeleteAgreement()}
                            disabled={selectedAgreement?.agreementStatus === AgreementStatus.Published}
                        >
                            Delete
                        </Button>
                    </Grid>
                </Grid>
            </form>
            {isShowingCopyDialog && (
                <CopyAgreementDialog
                    closeDialog={() => dispatch(showCopyDialog(false))}
                    agreementToCopy={selectedAgreement!}
                />
            )}
        </>
    );
};

export default connect(mapStateToProps)(AgreementDetails);
